import type { BankAccountGroupConnectionMemberSchema, BankAccountGroupSchema } from "@wayflyer/customer-bankconnect";
import { useState } from "react";

interface UseReassignedConnectionsForGroupProps {
  accountGroups: BankAccountGroupSchema[];
  originGroupUuid: string | null;
  // Seeds tracking from a snapshot recorded before a redirect-based connect (Basiq/mocked), whose
  // in-memory state would otherwise be wiped by the resulting full page reload.
  persistedMemberUuidsBeforeConnect: string[] | null;
}

export interface ReassignedConnectionMember {
  member: BankAccountGroupConnectionMemberSchema;
  destinationGroup: BankAccountGroupSchema;
}

interface UseReassignedConnectionsForGroupResult {
  reassignedMembers: ReassignedConnectionMember[];
  recordConnectAttempt: () => string[];
  clear: () => void;
}

// Tracks accounts that land in a different group than the one "Connect instantly" was launched
// from, so the launching panel can show them as "Reassigned" rather than silently dropping them.
export const useReassignedConnectionsForGroup = ({
  accountGroups,
  originGroupUuid,
  persistedMemberUuidsBeforeConnect,
}: UseReassignedConnectionsForGroupProps): UseReassignedConnectionsForGroupResult => {
  const [memberUuidsBeforeConnect, setMemberUuidsBeforeConnect] = useState<Set<string> | null>(() =>
    persistedMemberUuidsBeforeConnect ? new Set(persistedMemberUuidsBeforeConnect) : null
  );

  const recordConnectAttempt = (): string[] => {
    const memberUuids = accountGroups.flatMap((group) => group.connectionMembers.map((member) => member.memberUuid));
    setMemberUuidsBeforeConnect(new Set(memberUuids));
    return memberUuids;
  };

  const clear = (): void => setMemberUuidsBeforeConnect(null);

  const reassignedMembers: ReassignedConnectionMember[] =
    memberUuidsBeforeConnect && originGroupUuid
      ? accountGroups.flatMap((group) =>
          group.uuid === originGroupUuid
            ? []
            : group.connectionMembers
                .filter((member) => !memberUuidsBeforeConnect.has(member.memberUuid))
                .map((member) => ({ member, destinationGroup: group }))
        )
      : [];

  return { reassignedMembers, recordConnectAttempt, clear };
};
